import React, { useEffect, useRef, useState } from 'react';
import { Box, Radio } from '@mui/material';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import { PlayArrowRounded as PlayCircleRoundedIcon } from '@mui/icons-material';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import { useTranslation } from 'react-i18next';

import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CustomTypography from '@src/new-components/Typography';
import CustomButton from '@src/new-components/Button';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import { StyledAudioItem } from './index.style';

const PopupChangeVoice = ({
  openPopupSampleAudio,
  setOpenPopupSampleAudio,
  sampleAudios,
  selectedSampleVoice,
  setSelectedSampleVoice,
  draftSampleVoice,
  setDraftSampleVoice,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  const handlePlay = (item) => {
    if (audioRef.current) {
      if (audioRef.current.src !== item.audioLink) {
        audioRef.current.src = item.audioLink;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setPlayingId(item.id);
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  };

  const handleAudioEnd = () => {
    setPlayingId(null);
  };

  useEffect(() => {
    if (!openPopupSampleAudio && audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  }, [openPopupSampleAudio]);

  // useEffect(() => {
  //   if (selectedSampleVoice) {
  //     setDraftSampleVoice(selectedSampleVoice);
  //   }
  // }, [selectedSampleVoice, openPopupSampleAudio, setDraftSampleVoice]);

  const handleSave = () => {
    setSelectedSampleVoice(draftSampleVoice);
    setDraftSampleVoice(null);
    setOpenPopupSampleAudio(false);
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  };

  const handleCancel = () => {
    setDraftSampleVoice(null);
    setOpenPopupSampleAudio(false);
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  };

  const handleClose = () => {
    setDraftSampleVoice(null);
    setOpenPopupSampleAudio(false);
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  };

  const renderTitle = (
    <CustomTypography variant="body-20-bold" color={COLOR.darkGray}>
      {t('chooseSampleAudio')}
    </CustomTypography>
  );

  const renderAudioItem = (item, index) => {
    const isThisPlaying = playingId === item.id;
    return (
      <StyledAudioItem
        key={item.id}
        onClick={() => setDraftSampleVoice(item)}
        active={draftSampleVoice?.id === item.id}
      >
        <div className="audio-control-icon">
          {isThisPlaying ? (
            <Tooltip arrow title={t('pause')} placement="top">
              <PauseOutlinedIcon
                className="audio-control-icon-pause"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePause();
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip arrow title={t('playAllAudio')} placement="top">
              <PlayCircleRoundedIcon
                className="audio-control-icon-play"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlay(item);
                }}
              />
            </Tooltip>
          )}
        </div>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: '4px',
            }}
          >
            <CustomTypography
              variant="body-16-medium"
              color={COLOR.descriptionGray}
            >
              {t('Audio')} {index + 1}
            </CustomTypography>
            {selectedSampleVoice?.id === item.id && (
              <div className="audio-status">
                <CircleRoundedIcon
                  className="audio-status-icon"
                  sx={{
                    color: COLOR.yellow.default,
                    fontSize: '16px',
                  }}
                />
                <CustomTypography
                  variant="body-12-medium"
                  color={COLOR.descriptionGray}
                >
                  {t('selected')}
                </CustomTypography>
              </div>
            )}
          </Box>
          <CustomTypography
            variant="body-16-regular"
            color={COLOR.descriptionGray}
          >
            {item?.text}
          </CustomTypography>
        </Box>
        <Radio
          checked={draftSampleVoice?.id === item.id}
          sx={{
            color: COLOR.indigo.default,
            '&.Mui-checked': {
              color: COLOR.indigo.default,
            },
          }}
        />
      </StyledAudioItem>
    );
  };

  const renderActions = (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '24px',
      }}
    >
      <CustomButton
        variant="outlineSmall"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
        }}
        onClick={handleCancel}
      >
        {t('cancel')}
      </CustomButton>
      <CustomButton
        variant="contained"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
        }}
        onClick={handleSave}
      >
        {t('saveWhenLeave')}
      </CustomButton>
    </Box>
  );

  return (
    <>
      <CommonDialog
        open={openPopupSampleAudio}
        width="800px"
        styleTitle={{ paddingTitle: '12px 24px' }}
        PaperProps={{ sx: { height: '700px' } }}
        onClose={handleClose}
        renderTitle={renderTitle}
        contentComponent={sampleAudios?.map(renderAudioItem)}
        actionComponent={renderActions}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default PopupChangeVoice;
